import { useRef, useEffect, useCallback, useState, Component, ReactNode } from 'react';
import { useTheme } from '../../hooks/useTheme';
import keycloak from '../../services/keycloak';

const IFRAME_TIMEOUT_MS = 30000;

// Error Boundary
class IframeErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Something went wrong loading this module.</p>
          <button onClick={() => this.setState({ hasError: false })}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface IframeDashboardProps {
  src: string;
  title: string;
  queryParams?: Record<string, string>;
  redirectTo?: string;
}

function IframeDashboardInner({ src, title, queryParams = {}, redirectTo }: IframeDashboardProps) {
  const { theme } = useTheme();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeLoaded = useRef(false);
  const hasRedirected = useRef(false);
  const currentRedirectTo = useRef(redirectTo);
  const [loading, setLoading] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const [dynamicHeight, setDynamicHeight] = useState<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildIframeUrl = useCallback(() => {
    const themeValue = theme ? 'light' : 'dark';
    const params = new URLSearchParams({ theme: themeValue, ...queryParams });
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}${params.toString()}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, JSON.stringify(queryParams)]);

  const [iframeSrc] = useState(() => buildIframeUrl());

  // Timeout logic — starts on mount/redirect, only cleared on successful content load
  useEffect(() => {
    if (loading && !timedOut) {
      timeoutRef.current = setTimeout(() => {
        setTimedOut(true);
        setLoading(false);
      }, IFRAME_TIMEOUT_MS);
      return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
    }
  }, [loading, timedOut]);

  useEffect(() => {
    if (currentRedirectTo.current !== redirectTo) {
      currentRedirectTo.current = redirectTo;
      hasRedirected.current = false;
      iframeLoaded.current = false;
      setLoading(true);
      setTimedOut(false);
      if (iframeRef.current) {
        iframeRef.current.src = buildIframeUrl();
      }
    }
  }, [redirectTo, buildIframeUrl]);

  const sendThemeToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow && iframeLoaded.current) {
      const themeValue = theme ? 'light' : 'dark';
      iframeRef.current.contentWindow.postMessage(
        { type: 'SWOB_THEME_CHANGE', payload: { theme: themeValue } },
        '*'
      );
    }
  }, [theme]);

  // ── Keycloak Token Broadcasting ──────────────────────────────────────────
  const sendTokenToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow && keycloak.authenticated && keycloak.token) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'SWOB_AUTH_TOKEN', payload: { token: keycloak.token } },
        window.location.origin
      );
    }
  }, []);

  // Send token on refresh and broadcast logout
  useEffect(() => {
    const onTokenRefresh = () => {
      if (iframeRef.current?.contentWindow && iframeLoaded.current && keycloak.token) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'SWOB_TOKEN_REFRESH', payload: { token: keycloak.token } },
          window.location.origin
        );
      }
    };

    const onLogout = () => {
      if (iframeRef.current?.contentWindow && iframeLoaded.current) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'SWOB_AUTH_LOGOUT' },
          window.location.origin
        );
      }
    };

    keycloak.onAuthRefreshSuccess = onTokenRefresh;
    keycloak.onAuthLogout = onLogout;

    return () => {
      keycloak.onAuthRefreshSuccess = undefined;
      keycloak.onAuthLogout = undefined;
    };
  }, []);

  useEffect(() => {
    sendThemeToIframe();
  }, [sendThemeToIframe]);

  const handleIframeLoad = () => {
    iframeLoaded.current = true;
    if (redirectTo && !hasRedirected.current && iframeRef.current) {
      hasRedirected.current = true;
      iframeRef.current.src = redirectTo;
      return;
    }
    // Detect if iframe loaded an error page vs real content
    let loadedSuccessfully = true;
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const html = doc.documentElement?.innerHTML || '';
        // Traefik/nginx error pages are tiny (< 200 chars) and have no <script> or <app-root>
        if (html.length < 200 && !html.includes('<script')) {
          loadedSuccessfully = false;
        }
      }
    } catch {
      // Cross-origin = content loaded from a real server = success
    }

    if (loadedSuccessfully) {
      setLoading(false);
      setTimedOut(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      sendThemeToIframe();
      sendTokenToIframe();
      // Hide the legacy navbar inside the iframe
      try {
        const doc = iframeRef.current?.contentDocument;
        if (doc && !doc.getElementById('swob-embed-style')) {
          const style = doc.createElement('style');
          style.id = 'swob-embed-style';
          style.textContent = '.navbar { display: none !important; }';
          doc.head.appendChild(style);
        }
      } catch { /* cross-origin, skip */ }
    } else {
      // Show error immediately, don't wait for timeout
      setTimedOut(true);
      setLoading(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  };

  // Listen for messages: theme requests + height broadcasts
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate origin: only accept same-origin messages
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'SWOB_IFRAME_READY' || event.data?.type === 'SWOB_THEME_REQUEST') {
        sendThemeToIframe();
      }
      if (event.data?.type === 'SWOB_TOKEN_REQUEST') {
        sendTokenToIframe();
      }
      if (event.data?.type === 'SWOB_IFRAME_HEIGHT' && typeof event.data?.payload?.height === 'number') {
        setDynamicHeight(event.data.payload.height);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendThemeToIframe]);

  const handleRetry = () => {
    setTimedOut(false);
    setLoading(true);
    if (iframeRef.current) {
      iframeRef.current.src = buildIframeUrl();
    }
  };

  const bgColor = theme ? '#ffffff' : '#1a1a1a';

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: dynamicHeight ? `${dynamicHeight}px` : 'calc(100vh - 48px)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  };

  if (timedOut) {
    return (
      <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center', backgroundColor: bgColor }}>
        <p style={{ marginBottom: '1rem', color: theme ? '#333' : '#ccc' }}>
          The module took too long to load.
        </p>
        <button onClick={handleRetry} style={{ padding: '0.5rem 1.5rem', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center', backgroundColor: bgColor, zIndex: 10
        }}>
          <div className="loading-icon loading large"></div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title={title}
        onLoad={handleIframeLoad}
        sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-popups"
        style={{
          width: '100%', flex: 1, border: 'none', backgroundColor: bgColor,
          opacity: loading ? 0 : 1, transition: 'opacity 0.3s'
        }}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
}

export default function IframeDashboard(props: IframeDashboardProps) {
  return (
    <IframeErrorBoundary>
      <IframeDashboardInner {...props} />
    </IframeErrorBoundary>
  );
}
